import React from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { deleteCookie, useEffectOnce } from '@chilecompra/react-kit'
import { useAnalyticsPageView, useAnalyticsTag } from '@chilecompra/react-kit/analytics'
import { Button, Card, Select, Tab, Tabs, Typography, useSelect, useViewport } from '@chilecompra/react-kit/components'
import { useLanguage, useLanguageFlags } from '@chilecompra/react-kit/i18n'
import { languages } from '../../config/settings/i18n'
import routes from '../../config/settings/routes'
import ProtectByFeatures from '../../components/Security/ProtectByFeatures'
import { onRegisterBy, onSetForeignRegistration } from './RegisterWizard.actions'
import {
  Body,
  ButtonsContainer,
  ClaveUnicaImage,
  Column,
  Link,
  Root,
  Row,
  Section,
  Strong,
  Title,
  WelcomeImage,
  WarningIcon,
  SectionAlert
} from './RegisterWizardWelcome.styles'

/**
 * The RegisterWizardWelcome's container.
 */
const RegisterWizardWelcome = props => {
  const { onChangeStep } = props
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { language, onChange: onSwitchChange } = useLanguage({
    changeCallback: updatedLanguage => navigate(`/${updatedLanguage}/registrarme`, { replace: true })
  })
  const { hasLanguage, isLanguageES } = useLanguageFlags()
  const { size } = useViewport()
  const { value: languageValue, onChange: onLanguageChange } = useSelect({
    initialValue: isLanguageES ? 'es' : 'en',
    overwriteCallback: () => (isLanguageES ? 'es' : 'en')
  })
  const handleRegisterByUniquePass = () => {
    dispatch(onRegisterBy('by-unique-pass'))
    onChangeStep('by-unique-pass')
  }
  const handleClickSelectLanguage = () => onChangeStep('personal-info')
  const handleLanguageChange = option => {
    onLanguageChange(option)

    const updatedLanguage = option.target.value

    if (updatedLanguage === 'EN' || updatedLanguage === 'en') {
      return onSwitchChange('en')
    }

    return onSwitchChange('es')
  }
  const handleChangeRegisterType = type => {
    dispatch(onSetForeignRegistration(type === '1'))

    if (type === '1') {
      return onSwitchChange('es')
    }

    onSwitchChange('es')
    return navigate(routes.registerWizard, { replace: true })
  }

  useEffectOnce(() => {
    deleteCookie('registerWizardBackup')
  })

  const nationalInstructions = (
    <Section fontSize="13px" lineHeight="18px">
      <Strong>
        Si te registrarás como persona natural debes tener{' '}
        <Link href="http://www.sii.cl/servicios_online/1031-1034.html" rel="noreferrer" target="_blank">
          Inicio de Actividades
        </Link>
        <br />
        En caso de registrarte con tu empresa debes tener{' '}
        <Link href="http://www.sii.cl/destacados/tramites/clavetributaria.html" rel="noreferrer" target="_blank">
          Clave Tributaria
        </Link>
        <ProtectByFeatures rule={features => !features['com.seller/foreign.enrollment']}>
          <br />
          <br />
          Si quieres registrarte y tu empresa es extranjera, haz click{' '}
          <Link
            href="http://www.chileproveedores.cl/InscripcionyRegistro/Pasouno.aspx?user=V2305"
            rel="noreferrer"
            target="_blank"
          >
            aquí
          </Link>
        </ProtectByFeatures>
      </Strong>
    </Section>
  )

  useAnalyticsPageView('/landing')
  useAnalyticsTag({ page: 'registro_inicio' })

  return (
    <Root>
      <Body container direction={size.isTiny ? 'column-reverse' : 'row'} spacing={3}>
        <Column sm={6} xs={12}>
          <Title>{language('RegisterWizardWelcome__title')}</Title>
          <Section>
            {language('RegisterWizardWelcome__description__1')}
            <Strong>{language('RegisterWizardWelcome__description__2')}</Strong>{' '}
            {language('RegisterWizardWelcome__description__3')}
            <br />
            <br />
            {language('RegisterWizardWelcome__description__4')}{' '}
            <Strong>{language('RegisterWizardWelcome__description__5')} </Strong>
            {language('RegisterWizardWelcome__description__6')}
            <br />
            <br />
            <Strong>{language('RegisterWizardWelcome__question')}</Strong>
          </Section>
          <Section>
            <ProtectByFeatures
              fallback={() => (
                <ButtonsContainer>
                  <ProtectByFeatures rule={features => features['com.seller/unique.pass']}>
                    <Button color="primary" onClick={handleRegisterByUniquePass}>
                      <ClaveUnicaImage src="/assets/img/logo-clave-unica.svg" alt="Registrarse con ClaveÚnica" />
                    </Button>
                  </ProtectByFeatures>
                </ButtonsContainer>
              )}
              rule={features => features['com.seller/foreign.enrollment']}
            >
              <Tabs alignment="center" value={hasLanguage ? '1' : '0'} onChange={handleChangeRegisterType}>
                <Tab id="0" label={language('RegisterWizardWelcome__tab__national')}>
                  <Row alignItems="center" justifyContent="center" spacing={1}>
                    <ProtectByFeatures rule={features => features['com.seller/unique.pass']}>
                      <Column sm={8} xs={12}>
                        <Button color="primary" width="100%" onClick={handleRegisterByUniquePass}>
                          <ClaveUnicaImage src="/assets/img/logo-clave-unica.svg" alt="Registrarse con ClaveÚnica" />
                        </Button>
                      </Column>
                    </ProtectByFeatures>
                  </Row>
                </Tab>
                <Tab id="1" label={language('RegisterWizardWelcome__tab__foreign')}>
                  <Row alignItems="center" spacing={1}>
                    <Column md={4} xs={12}>
                      <Select
                        label="IDIOMA"
                        maxWidth="350px"
                        onChange={handleLanguageChange}
                        options={languages.map(language => ({ name: language.label, value: language.value }))}
                        value={languageValue}
                      />
                    </Column>
                    <Column md={8} xs={12}>
                      <Button color="primary" width="100%" onClick={handleClickSelectLanguage}>
                        {language('RegisterWizardWelcome__selectLanguageButton')}
                      </Button>
                    </Column>
                  </Row>
                  <Card color="warning" padding="16px 16px" margin="16px 0">
                    <SectionAlert>
                      <WarningIcon />
                      <Typography variant="body2">
                        <Strong>{language('RegisterWizardWelcome__alert__strong')}</Strong>
                        {language('RegisterWizardWelcome__alert__description')}
                      </Typography>
                    </SectionAlert>
                  </Card>
                </Tab>
              </Tabs>
            </ProtectByFeatures>
          </Section>
          <ProtectByFeatures
            fallback={() => nationalInstructions}
            rule={features => features['com.seller/foreign.enrollment']}
          >
            {hasLanguage ? (
              <Section fontSize="13px" lineHeight="18px" marginTop="0">
                {language('RegisterWizardWelcome__instructions__1')}
                <Link
                  href="https://ayuda.mercadopublico.cl/autoatencion/secciondeayuda/articulo/?c=CAT-01003&s=CAT-010032&a=KA-01705"
                  target="blank"
                >
                  {language('RegisterWizardWelcome__instructions__link')}
                </Link>
                {language('RegisterWizardWelcome__instructions__2')}
              </Section>
            ) : (
              nationalInstructions
            )}
          </ProtectByFeatures>
        </Column>
        <Column sm={6} xs={12}>
          <WelcomeImage src="/assets/img/steps/index-registro.svg" alt="Registrarse con Rut" />
        </Column>
      </Body>
    </Root>
  )
}

RegisterWizardWelcome.defaultProps = {
  onChangeStep: () => undefined
}

RegisterWizardWelcome.propTypes = {
  onChangeStep: PropTypes.func
}

export default RegisterWizardWelcome
